import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listKeywordSets, putKeywordSet, deleteKeywordSet, getDiscoverPlan, discoverKeywordSet,
} from "@/lib/adminApi";
import { apiErrorText, formatUsd } from "@/lib/adminHelpers";
import {
  AUTO_HINT, MAX_KEYWORDS, validateKeywordSet, setPayload, splitList, sourceLabel,
  keywordStatText, formatDate, discoverErrorText, discoverPlanText,
} from "@/lib/keywordSets";
import useNoindex from "@/lib/useNoindex";

export function KeywordSetCard({ set, onEdit, onDelete }) {
  const auto = set.source === "auto";
  return (
    <div className="rounded-md border p-4" data-testid={`kwset-${set.slug}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium">{set.name} <span className="text-xs text-muted-foreground">({set.slug})</span></div>
          <div className="text-xs text-muted-foreground">Kaynak: {sourceLabel(set)}</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onEdit(set)}>Düzenle</Button>
          <Button size="sm" variant="outline" onClick={() => onDelete(set)}>Sil</Button>
        </div>
      </div>
      <ul className="mt-2 list-disc pl-5 text-sm">
        {(set.keywords || []).map((k) => <li key={k}>{keywordStatText(set, k)}</li>)}
      </ul>
      {auto && (
        <div className="mt-2 text-xs text-muted-foreground" data-testid="kwset-auto-note">
          {AUTO_HINT}
          <div>Sektör kelimesi: {set.seed || "-"} · Oluşturulma: {formatDate(set.generated_at)}</div>
        </div>
      )}
    </div>
  );
}

function EditForm({ initial, isNew, onSaved, onCancel }) {
  const [slug, setSlug] = useState(initial.slug || "");
  const [name, setName] = useState(initial.name || "");
  const [kw, setKw] = useState((initial.keywords || []).join("\n"));
  const [mt, setMt] = useState((initial.match_terms || []).join("\n"));
  const [errs, setErrs] = useState([]);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const draft = { slug, name, match_terms: splitList(mt), keywords: splitList(kw) };
    const v = validateKeywordSet(draft);
    setErrs(v);
    if (v.length) return;
    setBusy(true);
    try {
      await putKeywordSet(slug, setPayload(draft));
      toast.success("Kaydedildi");
      onSaved();
    } catch (e) {
      toast.error(apiErrorText(e, "Kaydedilemedi"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 space-y-3 rounded-md border p-4" data-testid="kwset-form">
      <div>
        <Label htmlFor="kw-slug">Kısa ad (slug)</Label>
        <Input id="kw-slug" value={slug} disabled={!isNew} onChange={(e) => setSlug(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="kw-name">Ad</Label>
        <Input id="kw-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="kw-kw">Anahtar kelimeler (satır başına bir tane, en fazla {MAX_KEYWORDS})</Label>
        <textarea id="kw-kw" className="w-full rounded-md border bg-background p-2 text-sm" rows={5} value={kw} onChange={(e) => setKw(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="kw-mt">Eşleşme terimleri (kategori/arama metninde geçen; satır başına bir tane)</Label>
        <textarea id="kw-mt" className="w-full rounded-md border bg-background p-2 text-sm" rows={3} value={mt} onChange={(e) => setMt(e.target.value)} />
      </div>
      {errs.length > 0 && <ul className="list-disc pl-5 text-sm text-red-600" data-testid="kwset-errors">{errs.map((m) => <li key={m}>{m}</li>)}</ul>}
      <div className="flex gap-2">
        <Button onClick={save} disabled={busy}>Kaydet</Button>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Vazgeç</Button>
      </div>
    </div>
  );
}

export default function KeywordSets() {
  useNoindex();
  const [sets, setSets] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // {set, isNew}
  const [toDelete, setToDelete] = useState(null);
  const [seed, setSeed] = useState("");
  const [plan, setPlan] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try { setSets(await listKeywordSets()); setError(""); } catch (e) { setError(apiErrorText(e, "Kelime setleri yüklenemedi")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const askPlan = async () => {
    if (!seed.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      setPlan(await getDiscoverPlan(seed.trim()));
      setConfirmOpen(true);
    } catch (e) {
      toast.error(discoverErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const r = await discoverKeywordSet(seed.trim());
      setResult(r);
      toast.success(r.cached ? "Önbellekten alındı (harcama yok)" : "Sektör kelimeleri bulundu");
      load();
    } catch (e) {
      toast.error(discoverErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    const s = toDelete;
    setToDelete(null);
    try { await deleteKeywordSet(s.slug); toast.success("Silindi"); load(); } catch (e) { toast.error(apiErrorText(e, "Silinemedi")); }
  };

  return (
    <main className="mx-auto max-w-5xl px-4 py-6" data-testid="keywordsets-page">
      <h1 className="mb-1 text-xl font-semibold">Anahtar kelimeler</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Raporlarda ölçülen sektör kelimeleri (en fazla {MAX_KEYWORDS}). Elle kaydedilen set, otomatik seçileni her zaman geçersiz kılar.
      </p>

      <div className="mb-6 rounded-md border p-4" data-testid="kwset-discover">
        <div className="mb-2 text-sm font-medium">Sektör kelimelerini bul</div>
        <div className="flex flex-wrap gap-2">
          <Input className="max-w-xs" placeholder="örn. kfz gutachter" value={seed} disabled={busy} onChange={(e) => setSeed(e.target.value)} />
          <Button onClick={askPlan} disabled={busy || !seed.trim()} data-testid="kwset-discover-btn">
            {busy ? "Çalışıyor..." : "Sektör kelimelerini bul"}
          </Button>
        </div>
        {result && (
          <div className="mt-3 text-sm" data-testid="kwset-result">
            {result.cached ? "Önbellekten döndü, harcama yok. " : "Yeni sonuç kaydedildi. "}
            {result.keyword_set ? <KeywordSetCard set={result.keyword_set} onEdit={(s) => setEditing({ set: s, isNew: false })} onDelete={setToDelete} /> : "Uygun kelime bulunamadı."}
          </div>
        )}
      </div>

      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">Kayıtlı setler</h2>
        <Button size="sm" onClick={() => setEditing({ set: {}, isNew: true })}>Yeni set</Button>
      </div>
      {editing && (
        <EditForm
          key={editing.set.slug || "new"}
          initial={editing.set}
          isNew={editing.isNew}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {!sets && !error && <p className="text-sm text-muted-foreground">Yükleniyor...</p>}
      {sets && sets.length === 0 && <p className="text-sm text-muted-foreground">Henüz set yok.</p>}
      <div className="space-y-3">
        {(sets || []).map((s) => (
          <KeywordSetCard key={s.slug} set={s} onEdit={(x) => setEditing({ set: x, isNew: false })} onDelete={setToDelete} />
        ))}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gerçek para harcanabilir</AlertDialogTitle>
            <AlertDialogDescription>
              „{seed.trim()}“ için: {discoverPlanText(plan, formatUsd)} Bu gerçek paradır (DataForSEO + Claude). Devam etmek istiyor musunuz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={run} data-testid="kwset-discover-confirm">
              {plan && !plan.cached ? `Evet, ${formatUsd(plan.estimated_usd)} harca ve bul` : "Evet, getir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Seti sil</AlertDialogTitle>
            <AlertDialogDescription>„{toDelete && toDelete.name}“ ({toDelete && toDelete.slug}) silinecek. Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="kwset-delete-confirm">Evet, sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
